<template>
    <div class="barcode-scanner">
        <!-- Scanner Header -->
        <div class="scanner-header">
            <h3>Scan Food Barcode</h3>
            <button class="close-btn" @click="$emit('close')">×</button>
        </div>

        <!-- Camera/Scanner View -->
        <div class="scanner-container">
            <div v-if="!cameraActive && !manualMode" class="scanner-placeholder">
                <div class="scanner-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.5">
                        <rect x="3" y="3" width="7" height="7" rx="1"></rect>
                        <rect x="14" y="3" width="7" height="7" rx="1"></rect>
                        <rect x="3" y="14" width="7" height="7" rx="1"></rect>
                        <rect x="14" y="14" width="7" height="7" rx="1"></rect>
                        <line x1="12" y1="3" x2="12" y2="21"></line>
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                    </svg>
                </div>
                <p>Position barcode within the frame</p>
                <div class="scanner-actions">
                    <button class="btn-primary" @click="startCamera" :disabled="loading">
                        <span class="btn-icon">📷</span>
                        Start Camera
                    </button>
                    <button class="btn-secondary" @click="manualMode = true">
                        <span class="btn-icon">⌨️</span>
                        Enter Manually
                    </button>
                </div>
            </div>

            <!-- Video Stream -->
            <div v-if="cameraActive" class="camera-view">
                <video ref="videoElement" autoplay playsinline muted class="camera-stream"></video>
                <div class="scan-overlay">
                    <div class="scan-frame"></div>
                    <div class="scan-line" :class="{ active: scanning }"></div>
                </div>
                <div class="camera-controls">
                    <button class="btn-icon-only" @click="toggleFlash" v-if="hasFlash">
                        <span>{{ flashOn ? '🔦' : '💡' }}</span>
                    </button>
                    <button class="btn-icon-only" @click="switchCamera" v-if="hasMultipleCameras">
                        <span>🔄</span>
                    </button>
                    <button class="btn-stop" @click="stopCamera">
                        Stop Camera
                    </button>
                </div>
            </div>

            <!-- Manual Entry Mode -->
            <div v-if="manualMode && !cameraActive" class="manual-entry">
                <div class="manual-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.5">
                        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                        <line x1="6" y1="8" x2="6" y2="16"></line>
                        <line x1="10" y1="8" x2="10" y2="16"></line>
                        <line x1="14" y1="8" x2="14" y2="16"></line>
                        <line x1="18" y1="8" x2="18" y2="16"></line>
                    </svg>
                </div>
                <input v-model="manualBarcode" type="text" inputmode="numeric" pattern="[0-9]*"
                    placeholder="Enter barcode number" class="barcode-input" @keyup.enter="lookupManualBarcode" />
                <div class="manual-actions">
                    <button class="btn-secondary" @click="manualMode = false">
                        ← Back
                    </button>
                    <button class="btn-primary" @click="lookupManualBarcode" :disabled="!manualBarcode || loading">
                        {{ loading ? 'Looking up...' : 'Search' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Search Results -->
        <div v-if="searchResult" class="scan-result">
            <div class="result-header">
                <span class="result-badge success">✓ Found</span>
                <button class="clear-btn" @click="clearResult">Clear</button>
            </div>

            <div class="food-item">
                <div class="food-image" v-if="searchResult.photo">
                    <img :src="searchResult.photo.thumb" :alt="searchResult.food_name" />
                </div>
                <div class="food-image placeholder" v-else>
                    <span>🍽️</span>
                </div>

                <div class="food-info">
                    <h4 class="food-name">{{ searchResult.food_name }}</h4>
                    <p class="food-brand" v-if="searchResult.brand_name">{{ searchResult.brand_name }}</p>
                    <p class="food-serving">
                        {{ searchResult.serving_qty }} {{ searchResult.serving_unit }}
                        ({{ searchResult.serving_weight_grams }}g)
                    </p>
                </div>
            </div>

            <!-- Nutrition Summary -->
            <div class="nutrition-summary">
                <div class="macro-card calories">
                    <span class="macro-value">{{ formattedNutrition.calories }}</span>
                    <span class="macro-label">kcal</span>
                </div>
                <div class="macro-card carbs">
                    <span class="macro-value">{{ formattedNutrition.carbs }}g</span>
                    <span class="macro-label">Carbs</span>
                </div>
                <div class="macro-card protein">
                    <span class="macro-value">{{ formattedNutrition.protein }}g</span>
                    <span class="macro-label">Protein</span>
                </div>
                <div class="macro-card fat">
                    <span class="macro-value">{{ formattedNutrition.fat }}g</span>
                    <span class="macro-label">Fat</span>
                </div>
            </div>

            <!-- Serving Selector -->
            <div class="serving-selector">
                <label>Servings:</label>
                <div class="serving-controls">
                    <button class="serving-btn" @click="decreaseServings" :disabled="servings <= 0.5">−</button>
                    <input v-model.number="servings" type="number" min="0.5" step="0.5" class="serving-input" />
                    <button class="serving-btn" @click="increaseServings">+</button>
                </div>
            </div>

            <!-- Add to Meal -->
            <div class="meal-selector">
                <label>Add to:</label>
                <div class="meal-options">
                    <button v-for="meal in mealTypes" :key="meal.value" class="meal-option"
                        :class="{ selected: selectedMeal === meal.value }" @click="selectedMeal = meal.value">
                        <span class="meal-icon" :style="{ backgroundColor: meal.color }">{{ meal.symbol }}</span>
                        <span class="meal-name">{{ meal.label }}</span>
                    </button>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="result-actions">
                <button class="btn-favorite" @click="addToFavorites" :disabled="savingFavorite">
                    {{ isFavorite ? '★ Saved' : '☆ Favorite' }}
                </button>
                <button class="btn-add" @click="addToLog" :disabled="!selectedMeal || addingToLog">
                    {{ addingToLog ? 'Adding...' : '+ Add to Log' }}
                </button>
            </div>
        </div>

        <!-- Not Found State -->
        <div v-if="notFound" class="scan-result not-found">
            <div class="result-header">
                <span class="result-badge error">✗ Not Found</span>
            </div>
            <p class="not-found-message">
                We couldn't find this product in our database.
            </p>
            <div class="not-found-actions">
                <button class="btn-secondary" @click="clearResult">Try Again</button>
                <button class="btn-primary" @click="$emit('create-custom')">Create Custom Food</button>
            </div>
        </div>

        <!-- Recent Scans -->
        <div v-if="!searchResult && !notFound && recentScans.length > 0" class="recent-scans">
            <h4>Recent Scans</h4>
            <div class="recent-list">
                <div v-for="scan in recentScans" :key="scan.id" class="recent-item" @click="selectRecent(scan)">
                    <div class="recent-image" v-if="scan.photo">
                        <img :src="scan.photo.thumb" :alt="scan.food_name" />
                    </div>
                    <div class="recent-image placeholder" v-else>🍽️</div>
                    <div class="recent-info">
                        <span class="recent-name">{{ scan.food_name }}</span>
                        <span class="recent-brand" v-if="scan.brand_name">{{ scan.brand_name }}</span>
                    </div>
                    <span class="recent-cal">{{ scan.calories }}kcal</span>
                </div>
            </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message">
            <span class="error-icon">⚠️</span>
            <span>{{ error }}</span>
            <button class="dismiss-btn" @click="error = null">×</button>
        </div>
    </div>
</template>

<script>
import { FoodDatabase } from "@/services/food-database-service";
import { Nutrition } from "@/services/nutrition-service";

export default {
    name: "BarcodeScanner",

    props: {
        userId: {
            type: [Number, String],
            required: true,
        },
        date: {
            type: String,
            default: () => new Date().toISOString().split("T")[0],
        },
        defaultMealType: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            cameraActive: false,
            manualMode: false,
            manualBarcode: "",
            scanning: false,
            loading: false,
            searchResult: null,
            notFound: false,
            error: null,
            servings: 1,
            selectedMeal: null,
            recentScans: [],
            isFavorite: false,
            savingFavorite: false,
            addingToLog: false,
            hasFlash: false,
            flashOn: false,
            hasMultipleCameras: false,
            currentCamera: "environment",
            stream: null,
            barcodeDetector: null,
            scanInterval: null,
        };
    },

    computed: {
        mealTypes() {
            return Nutrition.getAllMealTypes();
        },

        formattedNutrition() {
            if (!this.searchResult) return {};
            const nutrition = FoodDatabase.formatNutrition(this.searchResult);
            return FoodDatabase.scaleByServings(nutrition, this.servings);
        },
    },

    watch: {
        defaultMealType: {
            immediate: true,
            handler(val) {
                if (val) this.selectedMeal = val;
            },
        },
    },

    async mounted() {
        this.loadRecentScans();
        this.initBarcodeDetector();
    },

    beforeUnmount() {
        this.stopCamera();
    },

    methods: {
        async initBarcodeDetector() {
            // Check if BarcodeDetector API is available
            if ("BarcodeDetector" in window) {
                try {
                    const formats = await window.BarcodeDetector.getSupportedFormats();
                    if (formats.includes("ean_13") || formats.includes("upc_a")) {
                        this.barcodeDetector = new window.BarcodeDetector({
                            formats: ["ean_13", "ean_8", "upc_a", "upc_e"],
                        });
                    }
                } catch (e) {
                    console.log("BarcodeDetector not supported:", e);
                }
            }
        },

        async startCamera() {
            try {
                this.loading = true;
                this.error = null;

                // Check for camera permissions
                const constraints = {
                    video: {
                        facingMode: this.currentCamera,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                    },
                };

                this.stream = await navigator.mediaDevices.getUserMedia(constraints);

                // Check for multiple cameras
                const devices = await navigator.mediaDevices.enumerateDevices();
                const cameras = devices.filter((d) => d.kind === "videoinput");
                this.hasMultipleCameras = cameras.length > 1;

                // Check for flash/torch capability
                const track = this.stream.getVideoTracks()[0];
                const capabilities = track.getCapabilities?.();
                this.hasFlash = capabilities?.torch || false;

                // Set cameraActive first so the video element is rendered in the DOM
                this.cameraActive = true;

                // Wait for DOM update, then set video source
                this.$nextTick(async () => {
                    if (this.$refs.videoElement) {
                        this.$refs.videoElement.srcObject = this.stream;
                        try {
                            await this.$refs.videoElement.play();
                        } catch (e) {
                            console.log('Video play error:', e);
                        }
                        this.startScanning();
                    }
                });
            } catch (err) {
                console.error("Camera error:", err);
                if (err.name === "NotAllowedError") {
                    this.error = "Camera permission denied. Please allow camera access.";
                } else if (err.name === "NotFoundError") {
                    this.error = "No camera found on this device.";
                } else {
                    this.error = "Could not start camera. Try manual entry.";
                }
            } finally {
                this.loading = false;
            }
        },

        stopCamera() {
            if (this.scanInterval) {
                clearInterval(this.scanInterval);
                this.scanInterval = null;
            }

            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop());
                this.stream = null;
            }

            this.cameraActive = false;
            this.scanning = false;
        },

        startScanning() {
            if (!this.barcodeDetector || !this.$refs.videoElement) {
                // Fall back to manual entry if no barcode detector
                console.log("BarcodeDetector not available, use manual entry");
                return;
            }

            this.scanning = true;

            this.scanInterval = setInterval(async () => {
                if (!this.cameraActive || this.loading) return;

                try {
                    const barcodes = await this.barcodeDetector.detect(this.$refs.videoElement);
                    if (barcodes.length > 0) {
                        const barcode = barcodes[0].rawValue;
                        this.stopCamera();
                        await this.lookupBarcode(barcode);
                    }
                } catch (e) {
                    // Ignore detection errors
                }
            }, 250);
        },

        async switchCamera() {
            this.currentCamera = this.currentCamera === "environment" ? "user" : "environment";
            this.stopCamera();
            await this.startCamera();
        },

        async toggleFlash() {
            if (!this.stream) return;

            const track = this.stream.getVideoTracks()[0];
            try {
                await track.applyConstraints({
                    advanced: [{ torch: !this.flashOn }],
                });
                this.flashOn = !this.flashOn;
            } catch (e) {
                console.error("Flash toggle failed:", e);
            }
        },

        async lookupBarcode(barcode) {
            const parsed = FoodDatabase.parseBarcode(barcode);
            if (!parsed) {
                this.error = "Invalid barcode format";
                return;
            }

            this.loading = true;
            this.error = null;
            this.notFound = false;

            try {
                const response = await FoodDatabase.lookupBarcode(parsed);

                if (response.data?.success && response.data?.food) {
                    this.searchResult = response.data.food;

                    // Save to scan history
                    await FoodDatabase.saveScanHistory(this.userId, {
                        barcode: parsed,
                        food_name: this.searchResult.food_name,
                        brand_name: this.searchResult.brand_name,
                        calories: this.searchResult.nf_calories,
                        photo: this.searchResult.photo,
                    });

                    this.loadRecentScans();
                } else {
                    this.notFound = true;
                }
            } catch (err) {
                console.error("Barcode lookup error:", err);
                this.error = "Failed to look up barcode. Please try again.";
            } finally {
                this.loading = false;
            }
        },

        async lookupManualBarcode() {
            if (!this.manualBarcode) return;
            await this.lookupBarcode(this.manualBarcode);
        },

        async loadRecentScans() {
            try {
                const response = await FoodDatabase.getRecentScans(this.userId, 5);
                if (response.data?.success) {
                    this.recentScans = response.data.scans || [];
                }
            } catch (err) {
                console.error("Error loading recent scans:", err);
            }
        },

        selectRecent(scan) {
            // Re-lookup the barcode to get full nutrition data
            if (scan.barcode) {
                this.lookupBarcode(scan.barcode);
            }
        },

        clearResult() {
            this.searchResult = null;
            this.notFound = false;
            this.servings = 1;
            this.manualBarcode = "";
            this.error = null;
        },

        increaseServings() {
            this.servings = Math.round((this.servings + 0.5) * 10) / 10;
        },

        decreaseServings() {
            if (this.servings > 0.5) {
                this.servings = Math.round((this.servings - 0.5) * 10) / 10;
            }
        },

        async addToFavorites() {
            if (!this.searchResult) return;

            this.savingFavorite = true;
            try {
                await FoodDatabase.addToFavorites(this.userId, {
                    food_name: this.searchResult.food_name,
                    brand_name: this.searchResult.brand_name,
                    barcode: this.searchResult.nix_item_id,
                    calories: this.searchResult.nf_calories,
                    protein: this.searchResult.nf_protein,
                    carbs: this.searchResult.nf_total_carbohydrate,
                    fat: this.searchResult.nf_total_fat,
                    serving_size: this.searchResult.serving_qty,
                    serving_unit: this.searchResult.serving_unit,
                    photo: this.searchResult.photo,
                });
                this.isFavorite = true;
            } catch (err) {
                console.error("Error adding to favorites:", err);
                this.error = "Failed to save favorite";
            } finally {
                this.savingFavorite = false;
            }
        },

        async addToLog() {
            if (!this.searchResult || !this.selectedMeal) return;

            this.addingToLog = true;
            try {
                const scaledNutrition = this.formattedNutrition;

                await Nutrition.logMeal({
                    user_id: this.userId,
                    meal_type: this.selectedMeal,
                    meal_date: this.date,
                    food_name: this.searchResult.food_name,
                    brand_name: this.searchResult.brand_name,
                    calories: scaledNutrition.calories,
                    carbs: scaledNutrition.carbs,
                    protein: scaledNutrition.protein,
                    fat: scaledNutrition.fat,
                    servings: this.servings,
                    source: "barcode_scan",
                });

                this.$emit("food-logged", {
                    food: this.searchResult,
                    nutrition: scaledNutrition,
                    mealType: this.selectedMeal,
                });

                this.clearResult();
            } catch (err) {
                console.error("Error logging food:", err);
                this.error = "Failed to log food. Please try again.";
            } finally {
                this.addingToLog = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.barcode-scanner {
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    max-width: 400px;
    margin: 0 auto;
}

.scanner-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .close-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: #f0f0f0;
        font-size: 1.2rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background: #e0e0e0;
        }
    }
}

.scanner-container {
    position: relative;
    min-height: 250px;
    background: #1a1a1a;
}

.scanner-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;

    .scanner-icon {
        color: #666;
        margin-bottom: 16px;
    }

    p {
        color: #999;
        margin: 0 0 24px 0;
    }
}

.scanner-actions {
    display: flex;
    gap: 12px;

    button {
        padding: 12px 20px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        border: none;
        transition: all 0.2s;

        .btn-icon {
            font-size: 1.1rem;
        }
    }

    .btn-primary {
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;

        &:hover:not(:disabled) {
            transform: translateY(-1px);
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .btn-secondary {
        background: #333;
        color: #fff;

        &:hover {
            background: #444;
        }
    }
}

.camera-view {
    position: relative;
    width: 100%;
    height: 300px;

    .camera-stream {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}

.scan-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.scan-frame {
    width: 80%;
    height: 120px;
    border: 2px solid rgba(255, 255, 255, 0.8);
    border-radius: 8px;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.scan-line {
    position: absolute;
    width: 70%;
    height: 2px;
    background: #28a745;
    box-shadow: 0 0 10px #28a745;
    opacity: 0;

    &.active {
        opacity: 1;
        animation: scan 2s linear infinite;
    }
}

@keyframes scan {

    0%,
    100% {
        top: calc(50% - 60px);
    }

    50% {
        top: calc(50% + 60px);
    }
}

.camera-controls {
    position: absolute;
    bottom: 16px;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    gap: 12px;

    .btn-icon-only {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.9);
        cursor: pointer;
        font-size: 1.2rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .btn-stop {
        padding: 10px 20px;
        border-radius: 20px;
        border: none;
        background: rgba(220, 53, 69, 0.9);
        color: white;
        font-weight: 600;
        cursor: pointer;
    }
}

.manual-entry {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px 20px;

    .manual-icon {
        color: #666;
        margin-bottom: 20px;
    }

    .barcode-input {
        width: 100%;
        max-width: 280px;
        padding: 14px 16px;
        border: 2px solid #333;
        border-radius: 8px;
        background: #fff;
        font-size: 1.1rem;
        text-align: center;
        letter-spacing: 2px;
        margin-bottom: 20px;

        &:focus {
            outline: none;
            border-color: #28a745;
        }
    }
}

.manual-actions {
    display: flex;
    gap: 12px;

    button {
        padding: 10px 24px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: all 0.2s;
    }

    .btn-secondary {
        background: #e0e0e0;
        color: #333;
    }

    .btn-primary {
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.scan-result {
    padding: 16px;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .result-badge {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 600;

        &.success {
            background: #d4edda;
            color: #28a745;
        }

        &.error {
            background: #f8d7da;
            color: #dc3545;
        }
    }

    .clear-btn {
        padding: 4px 12px;
        border: none;
        background: #f0f0f0;
        border-radius: 12px;
        font-size: 0.8rem;
        cursor: pointer;
    }
}

.food-item {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;

    .food-image {
        width: 60px;
        height: 60px;
        border-radius: 8px;
        overflow: hidden;
        background: #f5f5f5;
        flex-shrink: 0;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &.placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
        }
    }

    .food-info {
        flex: 1;
        min-width: 0;

        .food-name {
            margin: 0 0 4px 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .food-brand {
            margin: 0 0 4px 0;
            font-size: 0.85rem;
            color: #666;
        }

        .food-serving {
            margin: 0;
            font-size: 0.8rem;
            color: #999;
        }
    }
}

.nutrition-summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 16px;

    .macro-card {
        text-align: center;
        padding: 10px 8px;
        border-radius: 8px;
        background: #f8f9fa;

        .macro-value {
            display: block;
            font-size: 1rem;
            font-weight: 700;
        }

        .macro-label {
            font-size: 0.7rem;
            color: #666;
            text-transform: uppercase;
        }

        &.calories {
            background: rgba(253, 126, 20, 0.15);

            .macro-value {
                color: #fd7e14;
            }
        }

        &.carbs {
            background: rgba(255, 193, 7, 0.15);

            .macro-value {
                color: #cc9a06;
            }
        }

        &.protein {
            background: rgba(220, 53, 69, 0.15);

            .macro-value {
                color: #dc3545;
            }
        }

        &.fat {
            background: rgba(40, 167, 69, 0.15);

            .macro-value {
                color: #28a745;
            }
        }
    }
}

.serving-selector {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;

    label {
        font-weight: 500;
        color: #555;
    }

    .serving-controls {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
    }

    .serving-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 1px solid #ddd;
        background: white;
        font-size: 1.2rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover:not(:disabled) {
            background: #f0f0f0;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .serving-input {
        width: 50px;
        text-align: center;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 4px;
        font-weight: 600;
    }
}

.meal-selector {
    margin-bottom: 16px;

    label {
        display: block;
        font-weight: 500;
        color: #555;
        margin-bottom: 8px;
    }

    .meal-options {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .meal-option {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border: 2px solid #eee;
        border-radius: 8px;
        background: white;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            border-color: #ccc;
        }

        &.selected {
            border-color: #28a745;
            background: rgba(40, 167, 69, 0.05);
        }

        .meal-icon {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 0.7rem;
            font-weight: 700;
        }

        .meal-name {
            font-size: 0.85rem;
            font-weight: 500;
        }
    }
}

.result-actions {
    display: flex;
    gap: 12px;

    button {
        flex: 1;
        padding: 12px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: all 0.2s;

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    .btn-favorite {
        background: #fff3cd;
        color: #856404;

        &:hover:not(:disabled) {
            background: #ffe69c;
        }
    }

    .btn-add {
        background: linear-gradient(135deg, #28a745, #20c997);
        color: white;

        &:hover:not(:disabled) {
            transform: translateY(-1px);
        }
    }
}

.not-found {
    .not-found-message {
        text-align: center;
        color: #666;
        margin-bottom: 16px;
    }

    .not-found-actions {
        display: flex;
        gap: 12px;

        button {
            flex: 1;
            padding: 12px;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            border: none;
        }

        .btn-secondary {
            background: #e0e0e0;
            color: #333;
        }

        .btn-primary {
            background: linear-gradient(135deg, #007bff, #17a2b8);
            color: white;
        }
    }
}

.recent-scans {
    padding: 16px;
    border-top: 1px solid #eee;

    h4 {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        color: #666;
    }
}

.recent-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.recent-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
        background: #f8f9fa;
    }

    .recent-image {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        overflow: hidden;
        background: #f0f0f0;
        flex-shrink: 0;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &.placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .recent-info {
        flex: 1;
        min-width: 0;

        .recent-name {
            display: block;
            font-size: 0.9rem;
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .recent-brand {
            display: block;
            font-size: 0.75rem;
            color: #999;
        }
    }

    .recent-cal {
        font-size: 0.8rem;
        color: #666;
        font-weight: 500;
    }
}

.error-message {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    margin: 16px;
    background: #f8d7da;
    border-radius: 8px;
    color: #721c24;
    font-size: 0.9rem;

    .error-icon {
        flex-shrink: 0;
    }

    .dismiss-btn {
        margin-left: auto;
        padding: 4px 8px;
        border: none;
        background: transparent;
        cursor: pointer;
        font-size: 1.1rem;
    }
}
</style>
